import { useMatches } from 'react-router';

import type { DashboardQuery } from '@/data-sources/contract';

/**
 * The page being shown: the deepest matched route's id (its `handle.routeId`)
 * and params. Read from the leaf match so a layout above the page — the
 * filter scope, the conversations modal — sees the page's params too
 * (`useParams` only returns params matched up to the route calling it).
 */
export function useLeafRoute(): {
  routeId: string | undefined;
  params: Readonly<Record<string, string | undefined>>;
} {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle as { routeId?: string } | undefined;
    if (handle?.routeId) return { routeId: handle.routeId, params: matches[i]!.params };
  }
  return { routeId: undefined, params: {} };
}

/**
 * On a custom report, its id — the source scopes the report's widgets and
 * View details to the posts its campaign linked. Elsewhere `undefined`.
 */
export function useReportScope(): string | undefined {
  const { routeId, params } = useLeafRoute();
  return routeId === 'custom-report' ? params.reportId : undefined;
}

/** A page query, scoped to a report when there is one. */
export const withReportScope = (
  query: DashboardQuery,
  dashboardId: string | undefined,
): DashboardQuery => (dashboardId ? { ...query, dashboardId } : query);
